import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MEAL_DAY_COLORS, MEAL_BRAND_PRIMARY } from '@/utils/mealDayColors';

const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_HEADER_RGB = [
  MEAL_DAY_COLORS.Sunday,
  MEAL_DAY_COLORS.Monday,
  MEAL_DAY_COLORS.Tuesday,
  MEAL_DAY_COLORS.Wednesday,
  MEAL_DAY_COLORS.Thursday,
  MEAL_DAY_COLORS.Friday,
  MEAL_DAY_COLORS.Saturday,
].map(hexToRgb);

const PAGE_MARGIN = 36;
const FOOTER_Y_OFFSET = 22;

function hexToRgb(hex) {
  const value = parseInt(String(hex).replace('#', ''), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function chunkWeekRows(flatCells, formatCell) {
  const rows = [];
  for (let i = 0; i < flatCells.length; i += 7) {
    const week = flatCells.slice(i, i + 7);
    while (week.length < 7) {
      week.push(null);
    }
    rows.push(week.map((cell) => formatCell(cell)));
  }
  return rows;
}

function createLandscapeDoc() {
  return new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'letter',
  });
}

function pageLayout(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableWidth = pageWidth - PAGE_MARGIN * 2;
  const colWidth = tableWidth / 7;
  return { pageWidth, pageHeight, tableWidth, colWidth };
}

function columnStyles(colWidth) {
  const styles = {};
  for (let i = 0; i < 7; i++) {
    styles[i] = { cellWidth: colWidth };
  }
  return styles;
}

function drawCalendarTable(doc, startY, bodyRows) {
  const { pageHeight, tableWidth, colWidth } = pageLayout(doc);
  const footerTop = pageHeight - PAGE_MARGIN;
  const availableHeight = footerTop - startY;
  const rowCount = bodyRows.length + 1;
  const minRowHeight = Math.max(Math.floor(availableHeight / rowCount), 42);
  const fontSize = minRowHeight >= 58 ? 8 : minRowHeight >= 48 ? 7.5 : 7;

  autoTable(doc, {
    startY,
    margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
    tableWidth,
    head: [WEEKDAY_HEADERS],
    body: bodyRows,
    theme: 'grid',
    styles: {
      fontSize,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'top',
      lineColor: [180, 180, 180],
      lineWidth: 0.25,
      minCellHeight: minRowHeight,
    },
    headStyles: {
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      minCellHeight: Math.max(minRowHeight * 0.22, 16),
    },
    columnStyles: columnStyles(colWidth),
    didParseCell(data) {
      if (data.section === 'head' && data.column.index < WEEKDAY_HEADER_RGB.length) {
        data.cell.styles.fillColor = WEEKDAY_HEADER_RGB[data.column.index];
      }
    },
  });
}

function drawPdfFooter(doc) {
  const { pageWidth, pageHeight } = pageLayout(doc);
  const y = pageHeight - FOOTER_Y_OFFSET;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(95, 109, 120);
  doc.text('Order cutoff is 7:00 AM', PAGE_MARGIN, y);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...hexToRgb(MEAL_BRAND_PRIMARY));
  doc.text('mySchoolCafe', pageWidth - PAGE_MARGIN, y, { align: 'right' });
}

function drawMenuPdfFooter(doc) {
  const { pageWidth, pageHeight } = pageLayout(doc);
  const y = pageHeight - FOOTER_Y_OFFSET;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...hexToRgb(MEAL_BRAND_PRIMARY));
  doc.text('mySchoolCafe', pageWidth - PAGE_MARGIN, y, { align: 'right' });
}

function formatOrderCell(cell, showDescriptions, displayName) {
  if (!cell || !cell.dayOfMonth) {
    return '';
  }
  const lines = [String(cell.dayOfMonth)];
  if (cell.menuDay && cell.menuDay.title) {
    lines.push(displayName(cell.menuDay.title));
  }
  if (cell.menuDay && cell.menuDay.description && showDescriptions) {
    lines.push(displayName(cell.menuDay.description));
  }
  if (cell.order && Array.isArray(cell.order.items)) {
    cell.order.items.forEach((item) => {
      let title = item.title || '';
      if (item.qty != null && item.qty !== 1) {
        title += ` (${item.qty})`;
      }
      if (title) {
        lines.push(title);
      }
      if (showDescriptions && item.description) {
        lines.push(item.description);
      }
    });
  }
  return lines.join('\n');
}

function formatMenuCell(cell, config, currency) {
  if (!cell || cell.empty || !cell.day) {
    return '';
  }
  const lines = [String(cell.day)];
  const menu = cell.menu || {};
  if (menu.title) {
    lines.push(menu.title);
  }
  if (config.isShowMenuDescription && menu.description) {
    lines.push(menu.description);
  }
  if (Array.isArray(menu.choices)) {
    menu.choices.forEach((choice) => {
      let line = choice.title || '';
      if (config.isShowItemPrice && choice.price && choice.price.length) {
        line += ` ${currency(choice.price[0])}`;
      }
      if (line.trim()) {
        lines.push(line.trim());
      }
      if (config.isShowItemDescription && choice.description) {
        lines.push(choice.description);
      }
    });
  }
  return lines.join('\n');
}

export function buildOrderCalendarPdf({
  monthTitle,
  studentFirstName,
  studentTeacher,
  studentGrade,
  calendarCells,
  showDescriptions,
  displayName,
}) {
  const doc = createLandscapeDoc();
  const { pageWidth } = pageLayout(doc);
  const title = `${displayName(studentFirstName)}'s lunch orders`;
  const tableStartY = 56;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(46, 61, 79);
  doc.text(title, PAGE_MARGIN, 32);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(95, 109, 120);
  doc.text(`${displayName(studentTeacher)} · Grade ${studentGrade}`, PAGE_MARGIN, 46);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...hexToRgb(MEAL_BRAND_PRIMARY));
  doc.text(monthTitle, pageWidth / 2, 36, { align: 'center' });

  const bodyRows = chunkWeekRows(calendarCells, (cell) =>
    formatOrderCell(cell, showDescriptions, displayName)
  );
  drawCalendarTable(doc, tableStartY, bodyRows);
  drawPdfFooter(doc);

  return doc.output('blob');
}

export function buildMenuCalendarPdf({
  calendarTitle,
  monthTitle,
  emptyDays,
  dayMenus,
  year,
  month,
  tz,
  config,
  currency,
  calendarCellWeekdayName,
}) {
  const flatCells = [];
  for (let i = 0; i < emptyDays; i++) {
    flatCells.push({ empty: true });
  }
  for (let day = 1; day <= dayMenus.length; day++) {
    flatCells.push({
      day,
      menu: dayMenus[day - 1] || {},
      weekday: calendarCellWeekdayName(year, month, day, tz).toLowerCase(),
    });
  }

  const doc = createLandscapeDoc();
  const { pageWidth } = pageLayout(doc);
  const tableStartY = 52;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...hexToRgb(MEAL_BRAND_PRIMARY));
  doc.text(calendarTitle || 'Menu calendar', PAGE_MARGIN, 32);

  doc.setFontSize(18);
  doc.text(monthTitle, pageWidth / 2, 36, { align: 'center' });

  const bodyRows = chunkWeekRows(flatCells, (cell) => formatMenuCell(cell, config, currency));
  drawCalendarTable(doc, tableStartY, bodyRows);
  drawMenuPdfFooter(doc);

  return doc.output('blob');
}
